import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common/primitives';

/**
 * Wer sich anmeldet, und auf welchem Weg.
 *
 * ## Warum ein Vertrag und nicht nur ein Typ im Master
 *
 * Bis M6b war `@kraaapf/contracts` ausschließlich das, was Master und Worker
 * gemeinsam brauchen. Mit M7 kommt eine zweite Gegenstelle dazu: die beiden
 * Portale. Für sie gilt dieselbe Begründung wie für den Agent — ein Bruch am
 * Protokoll soll im Typecheck des Monorepos scheitern und nicht im Browser
 * eines Betreibers, der gerade eine Website löschen will.
 *
 * ## Die Trennung, die hier gebaut wird, bevor es sie braucht
 *
 * `subjectType` unterscheidet den Betreiber vom Kunden. In Stufe 1 gibt es nur
 * `admin`; `portal` entsteht in Stufe 3. Das Feld steht trotzdem schon jetzt in
 * Tabelle und Token, denn es ist die Grenze aus `docs/planning/portale-plan.md`:
 * Ein Kundentoken kommt an `/admin/*` nicht vorbei. Diese Prüfung nachträglich
 * einzuziehen hieße, jedes bereits ausgestellte Token als unentscheidbar zu
 * behandeln.
 */

/** Für wen ein Konto, eine Anmeldung und ein Token gilt. */
export const AUTH_SUBJECT_TYPES = {
  /** Betreiber dieser Plattform. Sieht alles, darf alles. */
  admin: 'admin',
  /** Kunde. Sieht ausschließlich seine eigenen Daten (ab Stufe 3). */
  portal: 'portal',
} as const;

export type AuthSubjectType = (typeof AUTH_SUBJECT_TYPES)[keyof typeof AUTH_SUBJECT_TYPES];

export const authSubjectTypeSchema = z.enum([AUTH_SUBJECT_TYPES.admin, AUTH_SUBJECT_TYPES.portal]);

/**
 * Der Weg, auf dem sich jemand ausweist.
 *
 * Ein Konto kann mehrere haben: Der Anker ist die E-Mail-Adresse, nicht der
 * Anbieter (Entscheidung B13). Wer sich über Google angemeldet hat, kann
 * später zusätzlich ein Passwort setzen, ohne ein zweites Konto zu bekommen.
 */
export const AUTH_PROVIDERS = {
  // Kein hinterlegtes Passwort, sondern der Name des Anmeldewegs: Wir selbst
  // sind der Anbieter. Die Regel kann das nicht unterscheiden.
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  password: 'password',
  google: 'google',
  microsoft: 'microsoft',
} as const;

export type AuthProvider = (typeof AUTH_PROVIDERS)[keyof typeof AUTH_PROVIDERS];

export const authProviderSchema = z.enum([
  AUTH_PROVIDERS.password,
  AUTH_PROVIDERS.google,
  AUTH_PROVIDERS.microsoft,
]);

/**
 * Die Rolle eines Kontos.
 *
 * Bewusst eine feste, sehr kurze Liste statt eines Rechtesystems: Es gibt einen
 * Betreiber und je Kunde genau einen Benutzer (Entscheidungen A1 und A2). Eine
 * Rechteverwaltung ohne zweiten Benutzer wäre Bedienoberfläche für einen Fall,
 * den es nicht gibt — und sie wäre der Ort, an dem später eine Lücke entsteht.
 */
export const AUTH_ROLES = {
  /** Betreiber. Nur in Verbindung mit `subjectType: admin`. */
  operator: 'operator',
  /** Kundenbenutzer. Nur in Verbindung mit `subjectType: portal`. */
  owner: 'owner',
} as const;

export type AuthRole = (typeof AUTH_ROLES)[keyof typeof AUTH_ROLES];

export const authRoleSchema = z.enum([AUTH_ROLES.operator, AUTH_ROLES.owner]);

/** E-Mail-Adresse, normalisiert auf Kleinschreibung. */
export const emailSchema = z
  .string()
  .min(3, { message: 'E-Mail-Adresse ist zu kurz.' })
  .max(254, { message: 'E-Mail-Adresse darf höchstens 254 Zeichen lang sein.' })
  .email({ message: 'Muss eine gültige E-Mail-Adresse sein.' })
  .transform((value) => value.toLowerCase());

/**
 * Das angemeldete Konto, wie die Oberfläche es sieht.
 *
 * Kein Passwort-Hash, kein TOTP-Geheimnis, keine Sitzungs-ID: Was hier steht,
 * steht im Speicher jedes Browsers. `totpEnabled` ist ein Wahrheitswert und
 * nicht das Geheimnis — die Oberfläche muss wissen, ob sie „einrichten" oder
 * „abschalten" anbietet, mehr nicht.
 */
export const currentUserSchema = z.object({
  id: uuidSchema,
  subjectType: authSubjectTypeSchema,
  role: authRoleSchema,
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  totpEnabled: z.boolean(),
  /** Die Anmeldewege, die für dieses Konto hinterlegt sind. */
  providers: z.array(authProviderSchema),
  lastLoginAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

export type CurrentUser = z.infer<typeof currentUserSchema>;

/**
 * Eine offene Anmeldung.
 *
 * Sichtbar im eigenen Konto, damit sich eine vergessene Sitzung zurückziehen
 * lässt. `current` markiert die Sitzung, mit der gerade gefragt wurde — ohne
 * sie träfe der Betreiber die Auswahl blind und meldete sich selbst ab.
 */
export const authSessionViewSchema = z.object({
  id: uuidSchema,
  current: z.boolean(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  lastUsedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
});

export type AuthSessionView = z.infer<typeof authSessionViewSchema>;

/**
 * Die Namen der beiden Cookies.
 *
 * Im Vertrag, weil sie an drei Stellen gebraucht werden: Der Master setzt sie,
 * das Abnahmeskript liest sie, und der Playwright-Lauf prüft sie. Drei Orte mit
 * je einer getippten Zeichenkette sind drei Gelegenheiten für einen Tippfehler,
 * der sich als „Anmeldung funktioniert nicht" äußert.
 *
 * Das Präfix `__Host-` bleibt bewusst weg: Es verlangt zwingend `Secure`, und
 * in der Entwicklung läuft die Oberfläche über `http://localhost`. Ein
 * Cookie-Name, der sich zwischen Entwicklung und Produktion unterscheidet, wäre
 * genau die Sorte Abweichung, die man erst im Rollout bemerkt.
 */
export const AUTH_COOKIE_NAMES = {
  access: 'kraaapf_access',
  refresh: 'kraaapf_refresh',
} as const;
